// TenantEditScreen.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, Button, Row, Col, Card, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useGetTenantDetailsQuery,
  useUpdateTenantMutation,
} from '../../slices/tenantsApiSlice';
import { useGetHousesQuery } from '../../slices/housesApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

const TenantEditScreen = () => {
  const { id: tenantId } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [contact, setContact] = useState('');
  const [type, setType] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [images, setImages] = useState([]);
  const [houseInput, setHouseInput] = useState('');
  const [filteredHouses, setFilteredHouses] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState(null);

  const [landlordInput, setLandlordInput] = useState('');
  const [filteredLandlords, setFilteredLandlords] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState(null);

  const { data: tenant, isLoading, error, refetch } = useGetTenantDetailsQuery(tenantId);
  const [updateTenant, { isLoading: savingTenant }] = useUpdateTenantMutation();
  const { data: housesData } = useGetHousesQuery({ keyword: houseInput });
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: landlordInput });

  useEffect(() => {
    if (tenant) {
      setName(tenant.name || '');
      setAddress(tenant.address || '');
      setContact(tenant.contact || '');
      setType(tenant.type || '');
      setMonthlyRent(tenant.monthlyRent || '');
      setSecurityDeposit(tenant.securityDeposit || '');
      setDescription(tenant.description || '');
      setStartDate(tenant.startDate?.substring(0, 10) || '');
      setImages(tenant.images || []);
      setSelectedHouse(tenant.house || null);
      setHouseInput(tenant.house?.name || '');

      setSelectedLandlord(tenant.landlord || null);
      setLandlordInput(tenant.landlord?.name || '');
    }
  }, [tenant]);

  useEffect(() => {
    if (housesData?.houses && houseInput.trim()) {
      const term = houseInput.toLowerCase();
      setFilteredHouses(
        housesData.houses.filter(h => h.name.toLowerCase().includes(term)).slice(0, 5)
      );
    } else {
      setFilteredHouses([]);
    }
  }, [houseInput, housesData]);

  useEffect(() => {
    if (landlordsData?.landlords && landlordInput.trim()) {
      const term = landlordInput.toLowerCase();
      setFilteredLandlords(
        landlordsData.landlords.filter(l => l.name.toLowerCase().includes(term)).slice(0, 5)
      );
    } else {
      setFilteredLandlords([]);
    }
  }, [landlordInput, landlordsData]);

  const handleHouseSelect = (house) => {
    setSelectedHouse(house);
    setHouseInput(house.name);
    setFilteredHouses([]);
  };

  const handleLandlordSelect = (landlord) => {
    setSelectedLandlord(landlord);
    setLandlordInput(landlord.name);
    setFilteredLandlords([]);
  };

  const handleImageChange = (index, value) => {
  const updated = [...images];
  updated[index] = value;
  setImages(updated);
};

const addImageField = () => {
  setImages((prev) => [...prev, '']);
};

const removeImageField = (index) => {
  setImages((prev) => prev.filter((_, i) => i !== index));
};

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!name || !selectedHouse || !selectedLandlord || !startDate) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await updateTenant({
        tenantId,
        name,
        house: selectedHouse._id,
        address,
        contact,
        type,
        monthlyRent: parseFloat(monthlyRent),
        securityDeposit: parseFloat(securityDeposit),
        description,
        landlord: selectedLandlord._id,
        startDate,
        images,
      }).unwrap();
      toast.success('Tenant updated');
      refetch();
      navigate('/admin/tenantlistscreen');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };
    return (
    <>
      <Link to="/admin/tenantlistscreen" className="btn btn-light my-3">Go Back</Link>
      <Row>
        <Col md={8}>
          <h1>Edit Tenant</h1>
          {isLoading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">{error?.data?.message || 'Failed to load tenant'}</Message>
          ) : (
            <Form onSubmit={submitHandler}>
              <Form.Group controlId="name" className="mb-3">
                <Form.Label>Tenant Name</Form.Label>
                <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </Form.Group>

              <Form.Group controlId="houseInput" className="mb-3">
                <Form.Label>House</Form.Label>
                <Form.Control
                  type="text"
                  value={houseInput}
                  onChange={(e) => setHouseInput(e.target.value)}
                  placeholder="Type to search house"
                  required
                />
                {filteredHouses.length > 0 && (
                  <ListGroup className="dropdown-menu mt-2 show">
                    {filteredHouses.map(h => (
                      <ListGroup.Item key={h._id} action onClick={() => handleHouseSelect(h)}>
                        {h.name}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                {selectedHouse && <div className="mt-2"><strong>Selected:</strong> {selectedHouse.name}</div>}
              </Form.Group>

              <Form.Group controlId="address" className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control type="text" value={address} onChange={(e) => setAddress(e.target.value)} required />
              </Form.Group>
              <Form.Group controlId="contact" className="mb-3">
                <Form.Label>Contact</Form.Label>
                <Form.Control type="text" value={contact} onChange={(e) => setContact(e.target.value)} required />
              </Form.Group>


              <Form.Group controlId="type" className="mb-3">
                <Form.Label>Unit Type</Form.Label>
                <Form.Control as="select" value={type} onChange={(e) => setType(e.target.value)} required>
                  <option value="">Select Type</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Shop">Single Room</option>
                  <option value="Single Room">Single Room</option>
                  <option value="Studio">Studio</option>
                  <option value="Shared">Shared</option>
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="monthlyRent" className="mb-3">
                <Form.Label>Monthly Rent</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={monthlyRent}
                  onChange={(e) => setMonthlyRent(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group controlId="securityDeposit" className="mb-3">
                <Form.Label>Security Deposit</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={securityDeposit}
                  onChange={(e) => setSecurityDeposit(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group controlId="description" className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              </Form.Group>

              <Form.Group controlId="landlordInput" className="mb-3">
                <Form.Label>Landlord</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordInput}
                  onChange={(e) => setLandlordInput(e.target.value)}
                  placeholder="Type to search landlord"
                  required
                />
                {filteredLandlords.length > 0 && (
                  <ListGroup className="dropdown-menu mt-2 show">
                    {filteredLandlords.map(l => (
                      <ListGroup.Item key={l._id} action onClick={() => handleLandlordSelect(l)}>
                        {l.name}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                {selectedLandlord && <div className="mt-2"><strong>Selected:</strong> {selectedLandlord.name}</div>}
              </Form.Group>


              <Form.Group controlId="startDate" className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
              </Form.Group>
              <Form.Group controlId="images" className="mb-3">
              <Form.Label>Upload Contract Images</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setImages([...e.target.files])}
              />
            </Form.Group>
            {images.length > 0 && (
              <div className="mt-3">
                <strong>Preview:</strong>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {Array.from(images).map((file, index) => (
                    <img
                      key={index}
                      src={URL.createObjectURL(file)}
                      alt={`preview-${index}`}
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  ))}
                </div>
              </div>
            )}

              <Button type="submit" variant="primary" className="mt-3">Update Tenant</Button>
            </Form>

            


          )}
        </Col>

        <Col md={4}>
          <Card className="mt-4">
            <ListGroup variant="flush">
              <ListGroup.Item>
                <strong>Quick Tips:</strong>
                <ul className="mb-0">
                  <li>Start typing to search houses and landlords.</li>
                  <li>Click a result to select it.</li>
                  <li>Ensure rent and start date are filled.</li>
                </ul>
              </ListGroup.Item>
              {savingTenant && (
                <ListGroup.Item>
                  <Loader />
                </ListGroup.Item>
              )}
           
                         
            </ListGroup>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default TenantEditScreen;